import React, { useEffect, useRef, useState } from 'react';
import { EditableOutlined } from '@duxton-web/icons';
import { Button, message, Popconfirm, Select, Tag } from '@duxton-web/ui';

import {
  Dimension,
  DimensionFilterConstraint,
  DimensionFilterValueType,
  DraftDimensionFilterConstraint,
} from '../../constants';
import {
  buildFreeTextDimensionFilterValue,
  normalizeFreeTextFilterValues,
} from '../../utils/dimension-filter';
import AppService from '@/lib/App';

type Props = {
  metricName: string;
  metricVersion?: number;
  dimension: Dimension | null;
  constraint: DimensionFilterConstraint | DraftDimensionFilterConstraint;
  readOnly: boolean;
  valueStatus?: 'error' | 'warning';
  onUpdate: (constraint: DraftDimensionFilterConstraint) => void;
};

export const FreeTextMetricFilterValue = ({
  metricName,
  metricVersion,
  dimension,
  constraint,
  readOnly,
  valueStatus,
  onUpdate,
}: Props) => {
  const [validating, setValidating] = useState(false);
  const [unverifiedValues, setUnverifiedValues] = useState<string[]>([]);
  const [warningOpen, setWarningOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const validationRequestRef = useRef(0);
  const selectRef = useRef<any>(null);
  const detectedTypeRef = useRef<DimensionFilterValueType | undefined>(
    constraint.type,
  );

  const values = normalizeFreeTextFilterValues(constraint.values);

  const cancelValidation = () => {
    validationRequestRef.current += 1;
    setValidating(false);
    setWarningOpen(false);
  };

  useEffect(() => {
    validationRequestRef.current += 1;
    detectedTypeRef.current = constraint.type;
    setValidating(false);
    setUnverifiedValues([]);
    setWarningOpen(false);
    setSearchValue('');
  }, [
    constraint.dimension_name,
    constraint.operator,
    metricName,
    metricVersion,
  ]);

  const getValuesWithSearchInput = () => {
    const searchValues = searchValue
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    return normalizeFreeTextFilterValues([...values, ...searchValues]);
  };

  const saveValues = (
    nextValues: string[],
    validationStatus: DraftDimensionFilterConstraint['validation_status'],
    valueType?: DraftDimensionFilterConstraint['type'],
  ) => {
    detectedTypeRef.current = valueType ?? detectedTypeRef.current;
    const valuePayload = buildFreeTextDimensionFilterValue(
      nextValues,
      detectedTypeRef.current ?? constraint.type,
    );

    if (valuePayload.removedValues?.length) {
      message.warning(
        `Invalid values removed: "${valuePayload.removedValues.join('", "')}". Expected type: ${valuePayload.type}.`,
      );
    }

    onUpdate({
      ...constraint,
      values: valuePayload.values,
      type: valuePayload.type,
      value_input_mode: 'FREE_TEXT',
      validation_status: validationStatus,
    });
  };

  const validateValues = async () => {
    const nextValues = getValuesWithSearchInput();
    setSearchValue('');

    if (!dimension || !constraint.operator || !nextValues.length) {
      return;
    }

    const requestId = validationRequestRef.current + 1;
    validationRequestRef.current = requestId;
    setValidating(true);
    setWarningOpen(false);
    setUnverifiedValues([]);

    saveValues(nextValues, undefined);

    try {
      const validation = await AppService.planner.validateDimensionFilterValues(
        metricName,
        {
          metricVersion,
          dimensionName: dimension.name,
          operator: constraint.operator,
          values: nextValues,
        },
      );

      if (validationRequestRef.current !== requestId) {
        return;
      }

      detectedTypeRef.current = validation?.type ?? detectedTypeRef.current;

      // Check if any valid values remain after type conversion
      const validatedPayload = buildFreeTextDimensionFilterValue(
        nextValues,
        validation?.type ?? detectedTypeRef.current,
      );
      if (!validatedPayload.values.length) {
        // All values were invalid for the detected type - save with correct type to remove them
        saveValues(nextValues, undefined, validation?.type);
        return;
      }

      const invalidValues = normalizeFreeTextFilterValues(
        validation?.unverified_values,
      );

      if (validation?.status === 'VERIFIED' && !invalidValues.length) {
        saveValues(nextValues, 'VERIFIED', validation?.type);
        message.success('Filter values saved');
        return;
      }

      setUnverifiedValues(invalidValues.length ? invalidValues : nextValues);
      setWarningOpen(true);
      saveValues(nextValues, undefined, validation?.type);
    } catch (_e) {
      if (validationRequestRef.current !== requestId) {
        return;
      }

      setUnverifiedValues(nextValues);
      setWarningOpen(true);
      saveValues(nextValues, undefined, 'STRING');
    } finally {
      if (validationRequestRef.current === requestId) {
        setValidating(false);
      }
    }
  };

  const handleValuesChange = (nextRawValues: string[]) => {
    const nextValues = normalizeFreeTextFilterValues(nextRawValues);
    cancelValidation();
    setSearchValue('');
    setUnverifiedValues([]);
    const valuePayload = buildFreeTextDimensionFilterValue(
      nextValues,
      detectedTypeRef.current ?? constraint.type ?? 'STRING',
    );

    if (valuePayload.removedValues?.length) {
      message.warning(
        `Invalid values removed: "${valuePayload.removedValues.join('", "')}". Expected type: ${valuePayload.type}.`,
      );
    }

    onUpdate({
      ...constraint,
      values: valuePayload.values.length ? valuePayload.values : null,
      type: valuePayload.type,
      value_input_mode: 'FREE_TEXT',
      validation_status: undefined,
    });
  };

  const handleConfirmUnverifiedValues = () => {
    const nextValues = getValuesWithSearchInput();
    setSearchValue('');
    saveValues(nextValues, 'UNVERIFIED');
    setUnverifiedValues([]);
    setWarningOpen(false);
    message.success('Filter values saved');
  };

  const handleCancelWarning = () => {
    setWarningOpen(false);
    setTimeout(() => selectRef.current?.focus?.(), 0);
  };

  const handleEditClick = () => {
    onUpdate({
      ...constraint,
      validation_status: undefined,
    });
    setTimeout(() => selectRef.current?.focus?.(), 0);
  };

  const renderTag = ({
    label,
    value,
    closable,
    onClose,
    isMaxTag: _isMaxTag,
    ...tagProps
  }) => {
    const normalizedValue = String(value).trim();
    const isUnverified =
      !readOnly && unverifiedValues.includes(normalizedValue);

    return (
      <Tag
        {...tagProps}
        closable={closable}
        color={isUnverified ? 'orange' : undefined}
        onClose={onClose}
        style={{
          alignItems: 'center',
          display: 'inline-flex',
          gap: 4,
          marginInlineEnd: 4,
        }}
      >
        {label}
      </Tag>
    );
  };

  const hasValuesToValidate = getValuesWithSearchInput().length > 0;
  const isSaved = constraint.validation_status !== undefined;

  return (
    <div style={{ display: 'flex', gap: 8, minWidth: 300 }}>
      <Select
        ref={selectRef}
        data-testid="dimension-values-free-text"
        mode="tags"
        style={{ minWidth: 300, flexGrow: 1 }}
        value={values}
        onChange={handleValuesChange}
        onSearch={setSearchValue}
        searchValue={searchValue}
        placeholder="Enter value"
        disabled={readOnly || !constraint.operator || validating || isSaved}
        tokenSeparators={[',']}
        options={[]}
        suffixIcon={null}
        open={false}
        status={valueStatus}
        tagRender={renderTag}
      />
      {!readOnly && (
        <>
          {isSaved ? (
            <Button
              data-testid="dimension-values-free-text-edit"
              size="small"
              style={{ alignSelf: 'stretch', height: 'auto' }}
              onClick={handleEditClick}
              icon={<EditableOutlined />}
            >
              Edit
            </Button>
          ) : (
            <Popconfirm
              placement="topRight"
              title="We detected some values that may be invalid (highlighted in orange). Do you want to save them anyway?"
              okText="Save anyway"
              cancelText="Change"
              open={warningOpen}
              onConfirm={handleConfirmUnverifiedValues}
              onCancel={handleCancelWarning}
            >
              <Button
                data-testid="dimension-values-free-text-save"
                size="small"
                loading={validating}
                disabled={!constraint.operator || !hasValuesToValidate}
                style={{ alignSelf: 'stretch', height: 'auto' }}
                onClick={validateValues}
              >
                Save
              </Button>
            </Popconfirm>
          )}
        </>
      )}
    </div>
  );
};
